import type { PaperZone } from './Homography';

/**
 * Rolling-window duration filter for paper zone classification.
 *
 * Prevents single-frame misclassifications from triggering alerts.
 * A zone must be sustained for a configurable fraction of the time window
 * before the filter outputs it.
 */
export class PaperGazeFilter {
  private history: { zone: PaperZone; t: number }[] = [];
  private readonly windowMs: number;
  private readonly outsideThreshold: number;

  /**
   * @param windowMs Rolling window duration in ms (default 2000)
   * @param outsideThreshold Fraction of window that must be 'confident_outside'
   *        before the filter confirms it (default 0.7 = 70%)
   */
  constructor(windowMs = 2000, outsideThreshold = 0.7) {
    this.windowMs = windowMs;
    this.outsideThreshold = outsideThreshold;
  }

  /**
   * Feed a per-frame raw zone and get the filtered (debounced) zone back.
   */
  update(zone: PaperZone, now = Date.now()): PaperZone {
    this.history.push({ zone, t: now });

    // Prune entries older than the window
    const cutoff = now - this.windowMs;
    while (this.history.length > 0 && this.history[0].t < cutoff) {
      this.history.shift();
    }

    if (this.history.length === 0) return zone;

    // Compute time-weighted fraction for each zone.
    // Each entry is assumed to last until the next entry (or until `now`).
    let insideMs = 0;
    let boundaryMs = 0;
    let outsideMs = 0;

    for (let i = 0; i < this.history.length; i++) {
      const entry = this.history[i];
      const nextT = i < this.history.length - 1 ? this.history[i + 1].t : now;
      const dt = nextT - entry.t;
      switch (entry.zone) {
        case 'confident_inside':  insideMs += dt; break;
        case 'boundary':          boundaryMs += dt; break;
        case 'confident_outside': outsideMs += dt; break;
      }
    }

    const total = insideMs + boundaryMs + outsideMs;
    if (total === 0) return zone;

    const outsideFrac = outsideMs / total;
    const insideFrac = insideMs / total;

    if (outsideFrac >= this.outsideThreshold) return 'confident_outside';
    if (insideFrac >= this.outsideThreshold) return 'confident_inside';
    return 'boundary';
  }

  reset(): void {
    this.history = [];
  }
}
